import { ComponentProps, FC, MouseEvent, ReactNode } from 'react';

import { TokenBaseStaticSpace } from '@hh.ru/magritte-design-tokens/types';
import {
    Breadcrumbs,
    BreadcrumbsItem,
    Flex,
    GridColumn,
    GridLayout,
    GridRow,
    Title,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { Link as SPALink } from '@hh.ru/redux-spa-middleware';

import styles from 'components/common/PageContentHeader/PageContentHeader.module.less';

interface PageContentHeaderProps {
    title: string;
    description?: string;
    additionalContent?: ReactNode;
    rightContent?: ReactNode;
    breadcrumbsConfig?: { to: string; children: string; onClick?: (e: MouseEvent) => void }[];
    vSpacingBottom?: TokenBaseStaticSpace;
    mobileRightContentInline?: boolean;
    mobileVSpacingTop?: TokenBaseStaticSpace;
}

type MobileBreadcrumbLinkProps = ComponentProps<typeof SPALink> & {
    onBreadcrumbClick?: (event: MouseEvent) => void;
};

const MobileBreadcrumbLink: FC<MobileBreadcrumbLinkProps> = ({ onBreadcrumbClick, ...props }) => (
    <SPALink
        {...props}
        onClick={
            onBreadcrumbClick
                ? (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onBreadcrumbClick(event);
                  }
                : undefined
        }
    />
);

export const PageContentHeader: FC<PageContentHeaderProps> = ({
    title,
    description,
    breadcrumbsConfig,
    additionalContent,
    rightContent,
    vSpacingBottom,
    mobileRightContentInline = false,
    mobileVSpacingTop = 40,
}) => {
    const { isMobile } = useBreakpoint();
    const isMobileContentStacked = isMobile && !mobileRightContentInline;

    const breadcrumbsVSpacing = isMobile ? 12 : 24;
    let contentVSpacing: TokenBaseStaticSpace = 40;
    if (breadcrumbsConfig) {
        contentVSpacing = breadcrumbsVSpacing;
    } else if (isMobile) {
        contentVSpacing = mobileVSpacingTop;
    }

    const leftContent = (
        <div className={styles.titleWrapper}>
            <Title Element="h3" size="large" description={description}>
                {title}
            </Title>
            {additionalContent && (
                <>
                    <VSpacing default={8} />
                    {additionalContent}
                </>
            )}
        </div>
    );

    return (
        <>
            {breadcrumbsConfig && (
                <div className={styles.breadcrumbsWrapper}>
                    <Breadcrumbs>
                        {breadcrumbsConfig.map((item, key) => (
                            <BreadcrumbsItem
                                Element={isMobile && item.onClick ? MobileBreadcrumbLink : SPALink}
                                key={key}
                                to={item.to}
                                // onBreadcrumbClick передаётся только для MobileBreadcrumbLink,
                                // чтобы обойти внутренний onClick в BreadcrumbsItem на мобилке
                                {...(isMobile && item.onClick ? { onBreadcrumbClick: item.onClick } : {})}
                                onClick={!isMobile ? item.onClick : undefined}
                            >
                                {item.children}
                            </BreadcrumbsItem>
                        ))}
                    </Breadcrumbs>
                </div>
            )}
            <VSpacing default={contentVSpacing} />
            <GridLayout>
                <GridRow>
                    <GridColumn xs={4} s={8} m={12}>
                        <Flex
                            align={isMobileContentStacked ? 'flex-start' : 'center'}
                            justify={isMobileContentStacked ? 'flex-start' : 'space-between'}
                            direction={isMobileContentStacked ? 'column' : 'row'}
                            gap={isMobileContentStacked ? 16 : 24}
                        >
                            {leftContent}
                            {rightContent}
                        </Flex>
                    </GridColumn>
                </GridRow>
            </GridLayout>
            {!!vSpacingBottom && <VSpacing default={vSpacingBottom} />}
        </>
    );
};
